import {useState, useEffect} from 'react'
import {useNavigate, useLocation, Link} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {ROUTES} from '../../../../_gori/constants'
import {LocationState} from '../../../../types/locationTypes'
import {clearLabelSessionStorage} from '../utils/sessionStorageUtils'
import {isValidGoriSignatureHash} from '../utils/validation'

type AuthMethod = 'photo' | 'upload' | 'code' | null

export function GoriSignatureMethodSelect() {
  const [selectedMethod, setSelectedMethod] = useState<AuthMethod>(null)
  const navigate = useNavigate()
  const location = useLocation() as {state?: LocationState}
  const intl = useIntl()
  const hash = location.state?.hash

  useEffect(() => {
    if (!hash || !isValidGoriSignatureHash(hash)) {
      navigate(ROUTES.GORI_SIGNATURE.BASE, {replace: true})
    }
  }, [hash, navigate])

  const handleNext = () => {
    if (selectedMethod === 'photo') {
      navigate(ROUTES.GORI_SIGNATURE.TAKE_PHOTO, {state: {hash}})
    } else if (selectedMethod === 'upload') {
      navigate(ROUTES.GORI_SIGNATURE.UPLOAD_PHOTO, {state: {hash}})
    }
  }

  return (
    <div className='gori-signature-method-select'>
      <div className='gori-signature-header-wrapper text-center'>
        <img
          src='/media/logos/gori_signature_logo.png'
          alt={intl.formatMessage({id: 'GORI_SIGNATURE_TITLE'})}
          className='gori-signature-logo mb-5'
        />
        <p className='text-dark'>{intl.formatMessage({id: 'GORI_SIGNATURE_CHOOSE_METHOD'})}</p>
      </div>

      <div>
        <div className='gori-signature-methods-container mb-5'>
          <div className='gori-signature-methods-grid'>
            <div
              className={`gori-signature-method-option ${
                selectedMethod === 'photo' ? 'selected' : ''
              }`}
              onClick={() => setSelectedMethod('photo')}
              onKeyDown={(e) => e.key === 'Enter' && setSelectedMethod('photo')}
              role='button'
              tabIndex={0}
              aria-pressed={selectedMethod === 'photo'}
            >
              <div className='gori-signature-method-icon'>
                <img src='/media/gori/gori-signature/phone.svg' alt='Take with camera' />
              </div>
              <div className='gori-signature-method-content'>
                <div className='gori-signature-method-label'>Option 1</div>
                <div className='gori-signature-method-title'>
                  {intl.formatMessage({id: 'GORI_SIGNATURE_TAKE_PHOTO'})}
                </div>
                <div className='gori-signature-method-desc'>
                  {intl.formatMessage({id: 'GORI_SIGNATURE_TAKE_PHOTO_DESC'})}
                </div>
              </div>
            </div>

            <div
              className={`gori-signature-method-option ${
                selectedMethod === 'upload' ? 'selected' : ''
              }`}
              onClick={() => setSelectedMethod('upload')}
              onKeyDown={(e) => e.key === 'Enter' && setSelectedMethod('upload')}
              role='button'
              tabIndex={0}
              aria-pressed={selectedMethod === 'upload'}
            >
              <div className='gori-signature-method-icon'>
                <img src='/media/gori/gori-signature/photo.svg' alt='Upload from device' />
              </div>
              <div className='gori-signature-method-content'>
                <div className='gori-signature-method-label'>Option 2</div>
                <div className='gori-signature-method-title'>
                  {intl.formatMessage({id: 'GORI_SIGNATURE_UPLOAD_PHOTO'})}
                </div>
                <div className='gori-signature-method-desc'>
                  {intl.formatMessage({id: 'GORI_SIGNATURE_UPLOAD_PHOTO_DESC'})}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='text-center'>
        <button
          type='button'
          disabled={!selectedMethod}
          onClick={handleNext}
          className='gori-signature-submit btn btn-outline btn-primary-deep hover-orange rounded-pill'
        >
          {intl.formatMessage({id: 'NEXT'})}
        </button>
        <div className='mt-4'>
          <Link
            to={ROUTES.GORI_SIGNATURE.HASH.replace(':hash', hash)}
            className='gori-signature-skip-link'
            onClick={() => {
              if (hash) clearLabelSessionStorage(hash)
            }}
          >
            Skip to manual review
          </Link>
        </div>
      </div>
    </div>
  )
}
